import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, ShoppingBag, Star, Trash2, Loader2, TrendingUp, IndianRupee, Settings as SettingsIcon, Upload, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { ProductFormDialog } from "@/components/ProductFormDialog";
import { formatINR, assetUrl, waLink } from "@/config";

const STATUSES = ["placed", "confirmed", "packed", "shipped", "delivered", "cancelled"];
const PAY_STATUSES = ["pending", "paid", "failed", "refunded"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const { refresh: refreshSettings } = useSettings();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pfOpen, setPfOpen] = useState(false);
  const [pfProduct, setPfProduct] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ category: "all", mode: "pct", direction: "increase", value: "", target: "both" });
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploading, setUploading] = useState("");
  const qrRef = useRef();
  const logoRef = useRef();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
  }, [admin]); // eslint-disable-line

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/orders").then((r) => setOrders(r.data)),
      api.get("/admin/products").then((r) => setProducts(r.data)),
      api.get("/admin/reviews").then((r) => setReviews(r.data)),
      api.get("/admin/settings").then((r) => setSettings(r.data)),
      api.get("/categories").then((r) => setCategories(r.data)),
    ]).catch(() => toast.error("Failed to load data")).finally(() => setLoading(false));
  };
  useEffect(() => { if (admin && admin !== false) loadAll(); }, [admin]); // eslint-disable-line

  if (!admin || admin === false) return null;

  const revenue = orders.filter((o) => o.payment_status === "paid").reduce((a, o) => a + o.total, 0);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      await refreshSettings();
      toast.success("Store settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, [field]: data.url }));
      toast.success("Image uploaded — remember to Save");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading("");
    }
  };

  const updateOrder = async (orderId, patch) => {
    await api.patch(`/admin/orders/${orderId}`, patch);
    setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, ...patch } : o)));
    toast.success("Order updated");
  };

  const deleteProduct = async (slug) => {
    await api.delete(`/admin/products/${slug}`);
    setProducts((prev) => prev.filter((p) => p.slug !== slug));
    toast.success("Product deleted");
  };

  const toggleStock = async (p) => {
    const upd = { ...p, in_stock: !p.in_stock };
    await api.put(`/admin/products/${p.slug}`, upd);
    setProducts((prev) => prev.map((x) => (x.slug === p.slug ? upd : x)));
  };

  const toggleFeature = async (r) => {
    await api.patch(`/admin/reviews/${r.id}`, { featured: !r.featured });
    setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, featured: !x.featured } : x)));
  };

  const applyBulk = async () => {
    if (!bulk.value || Number(bulk.value) <= 0) return toast.error("Enter a value greater than 0");
    setApplyingBulk(true);
    try {
      const { data } = await api.post("/admin/products/bulk-price", { ...bulk, value: Number(bulk.value) });
      toast.success(`Updated prices on ${data.updated} product(s)`);
      setBulkOpen(false);
      setBulk({ category: "all", mode: "pct", direction: "increase", value: "", target: "both" });
      loadAll();
    } catch {
      toast.error("Bulk update failed");
    } finally {
      setApplyingBulk(false);
    }
  };

  const TABS = [
    { key: "orders", label: "Orders", icon: ShoppingBag, count: orders.length },
    { key: "products", label: "Products", icon: Package, count: products.length },
    { key: "reviews", label: "Reviews", icon: Star, count: reviews.length },
    { key: "settings", label: "Settings", icon: SettingsIcon, count: null },
  ];

  const STATS = [
    { label: "Total Orders", value: orders.length, icon: ShoppingBag },
    { label: "Paid Revenue", value: formatINR(revenue), icon: IndianRupee },
    { label: "Products", value: products.length, icon: Package },
    { label: "Reviews", value: reviews.length, icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <header className="bg-[#1C120C] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <p className="font-display text-xl font-bold">Sonia <span className="text-[#D97706]">Admin</span></p>
            <p className="text-xs text-[#B9A794]">{admin.email}</p>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-semibold">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-[#E7E0D6] p-5">
              <s.icon size={22} className="text-[#C2410C]" />
              <p className="font-display text-2xl font-bold text-[#1F130B] mt-3">{s.value}</p>
              <p className="text-xs text-[#6B5B52]">{s.label}</p>
            </div>
          ))}
        </div>

        {/* tabs */}
        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${tab === t.key ? "bg-[#C2410C] text-white border-[#C2410C]" : "bg-white text-[#6B5B52] border-[#E7E0D6]"}`}
            >
              <t.icon size={16} /> {t.label} {t.count != null && <span className="opacity-70">({t.count})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#C2410C]" size={32} /></div>
        ) : (
          <>
            {tab === "orders" && (
              <div className="space-y-3" data-testid="admin-orders">
                {orders.length === 0 && <p className="text-[#6B5B52]">No orders yet.</p>}
                {orders.map((o) => (
                  <div key={o.order_id} className="rounded-2xl bg-white border border-[#E7E0D6] p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#1F130B]">{o.order_id}</p>
                        <p className="text-xs text-[#6B5B52]">{o.customer.name} · {o.customer.mobile} · {o.customer.city}</p>
                        <p className="text-xs text-[#9CA3AF] mt-0.5">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-display text-lg font-bold text-[#C2410C]">{formatINR(o.total)}</p>
                        <select value={o.status} onChange={(e) => updateOrder(o.order_id, { status: e.target.value })} data-testid={`order-status-${o.order_id}`} className="rounded-lg border border-[#E7E0D6] bg-[#FAF6F0] px-2 py-1.5 text-xs capitalize">
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={o.payment_status} onChange={(e) => updateOrder(o.order_id, { payment_status: e.target.value })} data-testid={`order-pay-${o.order_id}`} className="rounded-lg border border-[#E7E0D6] bg-[#FAF6F0] px-2 py-1.5 text-xs capitalize">
                          {PAY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <a
                          href={`https://wa.me/91${o.customer.mobile.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi ${o.customer.name}, update on your Sonia Dry Fruits order ${o.order_id}: status is now "${o.status}". Total ${formatINR(o.total)}. Thank you!`)}`}
                          target="_blank" rel="noreferrer"
                          data-testid={`order-wa-${o.order_id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-[#25D366] text-white px-3 py-1.5 text-xs font-semibold"
                        >
                          Notify
                        </a>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-dashed border-[#E7E0D6] flex flex-wrap gap-2">
                      {o.items.map((it, i) => (
                        <span key={i} className="text-xs bg-[#F3ECE2] rounded-full px-3 py-1 text-[#6B5B52]">{it.name} ({it.weight_label}) ×{it.qty}</span>
                      ))}
                      <span className="text-xs bg-[#FEF3C7] rounded-full px-3 py-1 text-[#9A3412] capitalize">{o.payment_method}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "products" && (
              <div data-testid="admin-products">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <p className="text-sm text-[#6B5B52]">{products.length} products</p>
                  <div className="flex gap-2">
                    <button onClick={() => setBulkOpen((b) => !b)} data-testid="bulk-price-toggle" className="inline-flex items-center gap-2 rounded-full border-2 border-[#1C120C] text-[#1C120C] hover:bg-[#1C120C] hover:text-white font-semibold px-5 py-2.5 text-sm transition-colors">
                      <IndianRupee size={16} /> Bulk Price
                    </button>
                    <button onClick={() => { setPfProduct(null); setPfOpen(true); }} data-testid="add-product-btn" className="inline-flex items-center gap-2 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold px-5 py-2.5 text-sm">
                      <Package size={16} /> Add Product
                    </button>
                  </div>
                </div>

                {bulkOpen && (
                  <div className="rounded-2xl bg-white border-2 border-[#D97706] p-5 mb-5" data-testid="bulk-price-panel">
                    <h4 className="font-display text-lg font-bold text-[#1F130B] mb-3">Bulk Price Update</h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                      <div>
                        <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Category</label>
                        <select value={bulk.category} onChange={(e) => setBulk({ ...bulk, category: e.target.value })} data-testid="bulk-category" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2.5 text-sm outline-none">
                          <option value="all">All Categories</option>
                          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Direction</label>
                        <select value={bulk.direction} onChange={(e) => setBulk({ ...bulk, direction: e.target.value })} data-testid="bulk-direction" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2.5 text-sm outline-none">
                          <option value="increase">Increase</option>
                          <option value="decrease">Decrease</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">By</label>
                        <select value={bulk.mode} onChange={(e) => setBulk({ ...bulk, mode: e.target.value })} data-testid="bulk-mode" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2.5 text-sm outline-none">
                          <option value="pct">Percentage %</option>
                          <option value="flat">Fixed ₹</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Value</label>
                        <input type="number" min="0" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} placeholder={bulk.mode === "pct" ? "e.g. 10" : "e.g. 50"} data-testid="bulk-value" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2.5 text-sm outline-none focus:border-[#C2410C]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Apply to</label>
                        <select value={bulk.target} onChange={(e) => setBulk({ ...bulk, target: e.target.value })} data-testid="bulk-target" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2.5 text-sm outline-none">
                          <option value="both">Price + MRP</option>
                          <option value="price">Selling Price</option>
                          <option value="mrp">MRP only</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <button onClick={applyBulk} disabled={applyingBulk} data-testid="bulk-apply-btn" className="inline-flex items-center gap-2 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold px-6 py-2.5 disabled:opacity-60">
                        {applyingBulk ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Apply to {bulk.category === "all" ? "all products" : "category"}
                      </button>
                      <p className="text-xs text-[#9CA3AF]">Prices are rounded to nearest ₹10. This updates every weight option.</p>
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((p) => (
                  <div key={p.slug} className="rounded-2xl bg-white border border-[#E7E0D6] p-4 flex gap-3">
                    <img src={assetUrl(p.image)} alt={p.name} className="h-16 w-16 rounded-lg object-cover border border-[#E7E0D6]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[#1F130B] truncate">{p.name}</p>
                      <p className="text-xs text-[#6B5B52] capitalize">{p.category}</p>
                      <p className="text-sm font-bold text-[#C2410C] mt-0.5">{formatINR(p.weights[p.weights.length - 1].price)}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button onClick={() => { setPfProduct(p); setPfOpen(true); }} data-testid={`edit-product-${p.slug}`} className="text-xs rounded-full px-2.5 py-1 font-semibold bg-[#FEF3C7] text-[#9A3412]">Edit</button>
                        <button onClick={() => toggleStock(p)} data-testid={`toggle-stock-${p.slug}`} className={`text-xs rounded-full px-2.5 py-1 font-semibold ${p.in_stock ? "bg-[#EAF6EC] text-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>{p.in_stock ? "In Stock" : "Out of Stock"}</button>
                        <button onClick={() => deleteProduct(p.slug)} data-testid={`delete-product-${p.slug}`} className="text-[#9CA3AF] hover:text-[#C2410C]"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            )}

            {tab === "reviews" && (
              <div className="space-y-3" data-testid="admin-reviews">
                {reviews.length === 0 && <p className="text-[#6B5B52]">No reviews yet.</p>}
                {reviews.map((r) => (
                  <div key={r.id} className="rounded-2xl bg-white border border-[#E7E0D6] p-5 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-[#1F130B] text-sm">{r.name} · <span className="text-[#D97706]">{"★".repeat(r.rating)}</span></p>
                      <p className="text-xs text-[#9CA3AF]">{r.slug || "general"} · {new Date(r.created_at).toLocaleDateString("en-IN")}</p>
                      <p className="text-sm text-[#6B5B52] mt-1.5">{r.comment}</p>
                    </div>
                    <button onClick={() => toggleFeature(r)} data-testid={`feature-review-${r.id}`} className={`shrink-0 text-xs rounded-full px-3 py-1.5 font-semibold ${r.featured ? "bg-[#FEF3C7] text-[#9A3412]" : "bg-[#F3ECE2] text-[#6B5B52]"}`}>
                      {r.featured ? "★ Featured" : "Feature"}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {tab === "settings" && settings && (
              <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-settings">
                <div className="lg:col-span-2 rounded-2xl bg-white border border-[#E7E0D6] p-6 space-y-4">
                  <h3 className="font-display text-xl font-bold text-[#1F130B]">Store & Contact</h3>
                  {[
                    ["whatsapp_number", "WhatsApp Number (with country code)", "918809754990"],
                    ["phone", "Phone Number", "+91 88097 54990"],
                    ["email", "Email (admin only — hidden from customers)", "you@email.com"],
                    ["address", "Store Address", ""],
                    ["hours", "Business Hours", ""],
                    ["upi_id", "UPI ID", "7742004257@digikhata"],
                    ["upi_name", "UPI / Account Name", "Manisha Tate"],
                    ["instagram", "Instagram URL", "https://instagram.com/..."],
                    ["facebook", "Facebook URL", "https://facebook.com/..."],
                    ["youtube", "YouTube URL", "https://youtube.com/..."],
                  ].map(([k, label, ph]) => (
                    <div key={k}>
                      <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">{label}</label>
                      <input
                        value={settings[k] || ""}
                        onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
                        placeholder={ph}
                        data-testid={`settings-${k}`}
                        className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C]"
                      />
                    </div>
                  ))}
                  <button onClick={saveSettings} disabled={savingSettings} data-testid="settings-save-btn" className="inline-flex items-center gap-2 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold px-6 py-3 disabled:opacity-70">
                    {savingSettings ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Settings
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="rounded-2xl bg-white border border-[#E7E0D6] p-6 text-center">
                    <h4 className="font-display text-lg font-bold text-[#1F130B] mb-3">Payment QR</h4>
                    <img src={assetUrl(settings.qr_image)} alt="Payment QR" className="mx-auto w-44 h-44 object-contain rounded-xl border border-[#E7E0D6]" data-testid="settings-qr-preview" />
                    <input ref={qrRef} type="file" accept="image/*" hidden onChange={(e) => uploadImage(e.target.files[0], "qr_image")} />
                    <button onClick={() => qrRef.current?.click()} disabled={uploading === "qr_image"} data-testid="upload-qr-btn" className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-[#D97706] text-[#9A3412] hover:bg-[#FEF3C7] font-semibold px-5 py-2.5 text-sm disabled:opacity-60">
                      {uploading === "qr_image" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Change QR
                    </button>
                    <p className="mt-2 text-xs text-[#9CA3AF]">Then click Save Settings</p>
                  </div>
                  <div className="rounded-2xl bg-white border border-[#E7E0D6] p-6 text-center">
                    <h4 className="font-display text-lg font-bold text-[#1F130B] mb-3">Logo</h4>
                    <img src={assetUrl(settings.logo)} alt="Logo" className="mx-auto w-28 h-28 object-cover rounded-full border border-[#E7E0D6]" data-testid="settings-logo-preview" />
                    <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => uploadImage(e.target.files[0], "logo")} />
                    <button onClick={() => logoRef.current?.click()} disabled={uploading === "logo"} data-testid="upload-logo-btn" className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-[#D97706] text-[#9A3412] hover:bg-[#FEF3C7] font-semibold px-5 py-2.5 text-sm disabled:opacity-60">
                      {uploading === "logo" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Change Logo
                    </button>
                    <p className="mt-2 text-xs text-[#9CA3AF]">Then click Save Settings</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ProductFormDialog
        open={pfOpen}
        onClose={() => setPfOpen(false)}
        product={pfProduct}
        categories={categories}
        onSaved={loadAll}
      />
    </div>
  );
}
